import React, { useState, useEffect, useCallback } from "react";
import { 
  MapPin, TrendingUp, Heart, Users, 
  Building2, Flame, Trophy, RefreshCw, Wifi
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";

interface CityStats {
  city_id: string;
  city_name: string;
  country_code: string;
  country_name: string;
  country_flag: string;
  booth_count: number;
  participant_count: number;
  match_count: number;
  pop_count: number;
  hot_booths: number;
}

interface ExhibitionCityLeaderboardProps {
  className?: string;
  compact?: boolean;
}

const ExhibitionCityLeaderboard: React.FC<ExhibitionCityLeaderboardProps> = ({
  className,
  compact = false,
}) => {
  const [cityStats, setCityStats] = useState<CityStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState<"booths" | "participants" | "matches">("booths");
  const [isRealtime, setIsRealtime] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());

  const fetchCityStats = useCallback(async () => {
    try {
      // Get all cities with exhibition booths
      const { data: cities, error: citiesError } = await supabase
        .from("cities")
        .select(`
          id,
          name,
          country_code,
          countries!inner (
            name,
            flag
          )
        `)
        .eq("is_active", true);

      if (citiesError) throw citiesError;

      // Get booth stats for each city
      const statsPromises = (cities || []).map(async (city: any) => {
        // Get booth count and participants
        const { data: boothData } = await supabase
          .from("live_arenas")
          .select(`
            id,
            max_participants,
            arena_participants (
              id,
              left_at
            )
          `)
          .eq("city_id", city.id)
          .eq("is_active", true)
          .eq("is_exhibition_booth", true);

        const boothCount = boothData?.length || 0;
        const participantCount = boothData?.reduce((sum, booth: any) => 
          sum + (booth.arena_participants?.filter((p: any) => !p.left_at)?.length || 0), 0
        ) || 0;

        // Count hot booths (>70% capacity)
        const hotBooths = boothData?.filter((booth: any) => {
          const activeParticipants = booth.arena_participants?.filter((p: any) => !p.left_at)?.length || 0;
          const maxP = booth.max_participants || 10;
          return activeParticipants >= maxP * 0.7;
        }).length || 0;

        // Get matches in exhibition context
        const { count: matchCount } = await supabase
          .from("matches")
          .select("*", { count: "exact", head: true })
          .eq("met_context", "love_exhibition");

        // Get pops in exhibition context (approximate by booth interactions)
        const boothIds = boothData?.map((b: any) => b.id) || [];
        let popCount = 0;
        if (boothIds.length > 0) {
          const { count } = await supabase
            .from("arena_interactions")
            .select("*", { count: "exact", head: true })
            .in("arena_id", boothIds)
            .eq("interaction_type", "pop");
          popCount = count || 0;
        }

        return {
          city_id: city.id,
          city_name: city.name,
          country_code: city.country_code,
          country_name: city.countries?.name || "",
          country_flag: city.countries?.flag || "🌍",
          booth_count: boothCount,
          participant_count: participantCount,
          match_count: matchCount || 0,
          pop_count: popCount,
          hot_booths: hotBooths,
        };
      });

      const stats = await Promise.all(statsPromises);
      
      // Filter cities with activity and sort
      const activeStats = stats.filter(s => s.booth_count > 0 || s.participant_count > 0);
      setCityStats(activeStats);
      setLastUpdated(new Date());
    } catch (error) {
      console.error("Error fetching city stats:", error);
    }
  }, []);

  // Initial fetch
  useEffect(() => {
    setLoading(true);
    fetchCityStats().finally(() => setLoading(false));
  }, [fetchCityStats]);

  // Set up realtime subscriptions
  useEffect(() => {
    if (!isRealtime) return;

    // Subscribe to arena_participants changes
    const participantsChannel = supabase
      .channel("city-leaderboard-participants")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "arena_participants",
        },
        () => {
          console.log("Participant change detected, refreshing stats...");
          fetchCityStats();
        }
      )
      .subscribe();

    // Subscribe to live_arenas changes (booth creation/deletion)
    const arenasChannel = supabase
      .channel("city-leaderboard-arenas")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "live_arenas",
          filter: "is_exhibition_booth=eq.true",
        },
        () => {
          console.log("Arena change detected, refreshing stats...");
          fetchCityStats();
        }
      )
      .subscribe();

    // Subscribe to arena_interactions (pops)
    const interactionsChannel = supabase
      .channel("city-leaderboard-interactions")
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "arena_interactions",
        },
        () => {
          console.log("Interaction detected, refreshing stats...");
          fetchCityStats();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(participantsChannel);
      supabase.removeChannel(arenasChannel);
      supabase.removeChannel(interactionsChannel);
    };
  }, [isRealtime, fetchCityStats]);

  // Sort cities based on selected metric
  const sortedCities = [...cityStats].sort((a, b) => {
    switch (sortBy) {
      case "participants":
        return b.participant_count - a.participant_count;
      case "matches":
        return b.match_count - a.match_count;
      default:
        return b.booth_count - a.booth_count;
    }
  }).slice(0, compact ? 5 : 10);

  if (loading) {
    return (
      <div className={cn("flex items-center justify-center py-8", className)}>
        <div className="w-6 h-6 rounded-full border-2 border-pop border-t-transparent animate-spin" />
      </div>
    );
  }

  if (sortedCities.length === 0) {
    return (
      <div className={cn("text-center py-8", className)}>
        <MapPin className="w-10 h-10 mx-auto text-muted-foreground mb-2" />
        <p className="text-sm text-muted-foreground">No active cities yet</p>
      </div>
    );
  }

  return (
    <div className={cn("", className)}>
      {/* Header with sort tabs */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Trophy className="w-5 h-5 text-amber-500" />
          <h3 className="font-bold text-foreground">City Leaderboard</h3>
          {isRealtime && (
            <div className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-100 dark:bg-green-900/30">
              <Wifi className="w-3 h-3 text-green-600 animate-pulse" />
              <span className="text-xs text-green-700 dark:text-green-400 font-medium">Live</span>
            </div>
          )}
        </div>
        
        <div className="flex items-center gap-2">
          {!compact && (
            <>
              <button
                onClick={() => setIsRealtime(!isRealtime)}
                className={cn(
                  "p-1.5 rounded-lg transition-colors",
                  isRealtime 
                    ? "bg-green-100 dark:bg-green-900/30 text-green-600" 
                    : "bg-muted text-muted-foreground"
                )}
                title={isRealtime ? "Realtime enabled" : "Realtime disabled"}
              >
                <Wifi className="w-4 h-4" />
              </button>
              <button
                onClick={fetchCityStats}
                className="p-1.5 rounded-lg bg-muted text-muted-foreground hover:text-foreground transition-colors"
                title="Refresh now"
              >
                <RefreshCw className="w-4 h-4" />
              </button>
            </>
          )}
        </div>
      </div>

      {/* Sort tabs */}
      {!compact && (
        <div className="flex rounded-lg bg-muted/30 p-1 mb-4">
          {[
            { key: "booths", label: "Booths", icon: Building2 },
            { key: "participants", label: "People", icon: Users },
            { key: "matches", label: "Matches", icon: Heart },
          ].map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setSortBy(key as typeof sortBy)}
              className={cn(
                "flex-1 flex items-center justify-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium transition-colors",
                sortBy === key
                  ? "bg-white dark:bg-gray-800 shadow text-foreground"
                  : "text-muted-foreground hover:text-foreground"
              )}
            >
              <Icon className="w-3.5 h-3.5" />
              {label}
            </button>
          ))}
        </div>
      )}

      {/* Last updated */}
      {!compact && (
        <p className="text-xs text-muted-foreground mb-3">
          Last updated: {lastUpdated.toLocaleTimeString()}
        </p>
      )}

      {/* City list */}
      <div className="space-y-2">
        {sortedCities.map((city, index) => (
          <div
            key={city.city_id}
            className={cn(
              "flex items-center gap-3 p-3 rounded-xl transition-all",
              "bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm",
              "border border-white/60 dark:border-gray-700/60",
              index === 0 && "ring-2 ring-amber-400/50"
            )}
          >
            {/* Rank */}
            <div className={cn(
              "w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm",
              index === 0 && "bg-gradient-to-br from-amber-400 to-orange-500 text-white",
              index === 1 && "bg-gradient-to-br from-gray-300 to-gray-400 text-white",
              index === 2 && "bg-gradient-to-br from-amber-600 to-amber-700 text-white",
              index > 2 && "bg-muted text-muted-foreground"
            )}>
              {index + 1}
            </div>

            {/* City info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-lg">{city.country_flag}</span>
                <span className="font-semibold text-foreground truncate">
                  {city.city_name}
                </span>
                {city.hot_booths > 0 && (
                  <Flame className="w-4 h-4 text-orange-500 shrink-0 animate-pulse" />
                )}
              </div>
              <p className="text-xs text-muted-foreground truncate">
                {city.country_name}
              </p>
            </div>

            {/* Stats */}
            <div className="flex items-center gap-3 text-xs">
              <div className="text-center">
                <div className="flex items-center gap-1 text-primary font-bold">
                  <Building2 className="w-3.5 h-3.5" />
                  {city.booth_count}
                </div>
                <span className="text-muted-foreground">booths</span>
              </div>
              
              <div className="text-center">
                <div className="flex items-center gap-1 text-green-600 font-bold">
                  <Users className="w-3.5 h-3.5" />
                  {city.participant_count}
                </div>
                <span className="text-muted-foreground">live</span>
              </div>
              
              {!compact && (
                <div className="text-center">
                  <div className="flex items-center gap-1 text-pop font-bold">
                    <Heart className="w-3.5 h-3.5" />
                    {city.pop_count}
                  </div>
                  <span className="text-muted-foreground">pops</span>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* View all link for compact mode */}
      {compact && cityStats.length > 5 && (
        <button className="w-full mt-3 py-2 text-sm font-medium text-primary hover:underline">
          View all {cityStats.length} cities
        </button>
      )}
    </div>
  );
};

export default ExhibitionCityLeaderboard;
